/**
 * Time Log View Page — read-only detail.
 */

import * as React from 'react';
import { useParams, useNavigate } from 'react-router';
import { useOne } from '@refinedev/core';
import { Button, Card, CardContent, CardHeader, CardTitle, Badge } from '@yesbos/ui';
import { ArrowLeft, Edit } from 'lucide-react';

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <dt className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{label}</dt>
      <dd className="text-sm font-medium">{value ?? "—"}</dd>
    </div>
  );
}

export function TimeTrackerViewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading } = useOne({ resource: 'time_logs', id: id! });
  const log = data?.data as Record<string, unknown> | undefined;

  if (isLoading) return <div className="p-6 text-sm text-muted-foreground">Loading...</div>;
  if (!log) return <div className="p-6 text-sm text-destructive">Time log not found.</div>;

  return (
    <div className="p-6 max-w-xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/time-tracker')}><ArrowLeft className="h-4 w-4" /></Button>
          <h1 className="text-2xl font-semibold">Time Log</h1>
          <Badge variant="secondary">{String(log.status ?? 'draft')}</Badge>
          {log.is_billable && <Badge variant="default">Billable</Badge>}
        </div>
        <Button variant="outline" onClick={() => navigate(`/time-tracker/${id}/edit`)}>
          <Edit className="h-4 w-4 mr-2" />Edit
        </Button>
      </div>
      <Card>
        <CardHeader><CardTitle>Time Log Details</CardTitle></CardHeader>
        <CardContent>
          <dl className="grid grid-cols-2 gap-4">
            <Field label="Date" value={String(log.log_date ?? '').slice(0, 10)} />
            <Field label="Hours" value={`${log.hours}h`} />
            <Field label="Project" value={String(log.project_name ?? '')} />
            <Field label="Task" value={String(log.task_name ?? '')} />
            <Field label="Status" value={String(log.status ?? '')} />
            <Field label="Billable" value={log.is_billable ? 'Yes' : 'No'} />
          </dl>
          {log.description && (
            <div className="mt-4 space-y-1">
              <dt className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Description</dt>
              <dd className="text-sm">{String(log.description)}</dd>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default TimeTrackerViewPage;
